import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DOCUMENT, Location } from '@angular/common';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subject, take, takeUntil, switchMap, tap, map, catchError, of } from 'rxjs';
import { ColumnSelection, PrintColumnSelectionMode, PrintColumnSelectionResult } from '../../models/dialog.model';
import { PrintColumnSelectionDialogComponent } from '../print-column-selection-dialog/print-column-selection-dialog.component';
import { AppUtilityService } from '../../services/app-utility.service';
import { selectStudy } from '../../../app/state/selectors';
import { apiPaths } from '../../../app/core/constants/api.const';
import { setAuditTrail } from '../../../app/state/actions';
import { AuditTrailEntry } from '../../models/study.model';
/**
 * PrintExportDialogComponent
 * This component is responsible for rendering the print and export dialog. It allows users to select 
 * columns to include in a printable/exportable view and handles the print functionality.
 */
@Component({
  selector: 'app-print-export-dialog',
  templateUrl: './print-export-dialog.component.html',
  styleUrl: './print-export-dialog.component.scss'
})
export class PrintExportDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() tableColumns!: ColumnSelection[];
  @Input() autoOpen!: boolean;
  @Input() enableColumnSelection = true;
  @Input() columnSelectionMode: PrintColumnSelectionMode = 'kitLotInstrumentBead';
  @Input() menuHasBackdrop = true;
  @Input() viewName = '';
  @Output() selectColumns = new EventEmitter<ColumnSelection[]>();
  @Output() menuOpenStatus = new EventEmitter<boolean>();

  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;

  selectedColumns: ColumnSelection[] = [];
  isPrintDialogOpen = false;
  protected destroy$ = new Subject<boolean>();
  /**
   * 
   * @param doc Injecting the DOCUMENT to manipulate the DOM directly for print actions.
   * @param store Store
   */
  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private dialog: MatDialog,
    private location: Location,
    private appUtilityService: AppUtilityService,
    private store: Store) { }
  /** ngOnInit: Component lifecycle hook for initialization. */
  ngOnInit(): void {
    this.selectedColumns = this.tableColumns ?? [];

    window.onafterprint = () => {
      const overlayElements = this.doc.querySelectorAll('.cdk-overlay-pane');
      const overlay = overlayElements && overlayElements.length > 0 ? overlayElements[0] as HTMLDivElement : null;

      if (overlay) {
        overlay.style.visibility = 'visible';
      }

      this.isPrintDialogOpen = false;
    };
  }
  /** ngAfterViewInit: Lifecycle hook called after the view is initialized. */
  ngAfterViewInit() {
    if (this.autoOpen) {
      this.openMenu();
      this.updateMenuOpenStatus(true);
    }
  }
  /** Method to programmatically open the menu. */
  openMenu() {
    this.trigger.openMenu();
  }
  /** Method to update the menu open status. */
  updateMenuOpenStatus(isOpen: boolean) {
    this.selectColumns.emit(this.tableColumns);

    if (!this.isPrintDialogOpen) {
      this.menuOpenStatus.emit(isOpen);
    }
  }

  openColumnSelectionDialog(): void {
    if (!this.enableColumnSelection) {
      return;
    }
    const dialogRef = this.dialog.open(PrintColumnSelectionDialogComponent, {
      panelClass: 'bpx-dialog-container',
      data: {
        columns: this.tableColumns,
        mode: this.columnSelectionMode
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PrintColumnSelectionResult | undefined) => {
        // This menu prints from the browser, so it reads only the columns; the half
        // selection is for the server-rendered report.
        const updatedColumns: ColumnSelection[] | undefined = result?.columns;
        if (updatedColumns?.length) {
          this.tableColumns = updatedColumns;
          this.selectedColumns = updatedColumns;
          this.selectColumns.emit(updatedColumns);
        }
      });
  }
  /** Method to open the print dialog. */
  openPrinterDialog() {
    this.isPrintDialogOpen = true;

    const overlayElements = this.doc.querySelectorAll('.cdk-overlay-pane');
    const overlay = overlayElements && overlayElements.length > 0 ? overlayElements[0] as HTMLDivElement : null;

    if (overlay) {
      overlay.style.visibility = 'hidden';
    }

    this.logPrintAuditTrail().pipe(take(1)).subscribe(() => {
      setTimeout(() => {
        window.print();
      }, 500);
    });
  }

  private logPrintAuditTrail(): Observable<void> {
    return this.store.select(selectStudy).pipe(
      take(1),
      switchMap(study => {
        const payload = {
          run_name: study.studyName,
          action: 'Print',
          comment: this.viewName
        };
        const url = this.appUtilityService.getAPI(apiPaths.study.auditTrail);
        return this.appUtilityService.post<typeof payload, { audit_trail: AuditTrailEntry[] }>(url, payload, true).pipe(
          catchError(() => of(null))
        );
      }),
      tap(response => {
        if (response?.audit_trail) {
          this.store.dispatch(setAuditTrail({ auditTrail: response.audit_trail }));
        }
      }),
      map(() => undefined)
    );
  }

  goBack(): void {
    this.location.back();
  }
  /** ngOnDestroy: Cleanup lifecycle hook to unsubscribe from observables. */
  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
